import { useAuthStore } from "../../stores/authStore";
import { getPortfolioAccounts, type AccountSummary } from "../../services/portfolioService";
import type { Quote } from "../../services/pulseService";
import { usePolling } from "./usePolling";
import { changeColor, changeBg, formatPercent } from "./utils";

interface Props {
  spyQuote?: Quote;
}

export function AccountDailyPL({ spyQuote }: Props) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const { data, isLoading } = usePolling({
    fetcher: getPortfolioAccounts,
    interval: 60_000,
    enabled: isAuthenticated,
  });

  if (!isAuthenticated || (!data && !isLoading)) return null;

  const accounts = data?.accounts || [];
  const spyChangePct = spyQuote?.change_percent ?? 0;

  return (
    <div className="glass-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-surface-200 uppercase tracking-wider mb-3 flex items-center gap-2">
        My Accounts vs SPY
        <span className="text-xs font-normal text-surface-500 normal-case tracking-normal">
          Today
        </span>
      </h3>

      {isLoading && !data ? (
        <div className="flex items-center justify-center py-6">
          <div className="flex items-center gap-2 text-surface-500 text-xs">
            <div className="h-2 w-2 rounded-full bg-surface-600 animate-pulse" />
            Loading accounts...
          </div>
        </div>
      ) : accounts.length === 0 ? (
        <div className="text-xs text-surface-500 text-center py-4">
          No active accounts
        </div>
      ) : (
        <div className="space-y-1">
          {/* SPY benchmark row */}
          <BenchmarkRow label="SPY" changePct={spyChangePct} />

          {/* Divider */}
          <div className="border-t border-surface-700/50 my-2" />

          {/* Account rows */}
          {accounts.map((account) => (
            <AccountRow key={account.id} account={account} spyChangePct={spyChangePct} />
          ))}

          {/* Total row if multiple accounts */}
          {accounts.length > 1 && data && (
            <>
              <div className="border-t border-surface-700/50 my-2" />
              <TotalRow
                totalEquity={data.total_equity}
                todayChange={data.today_change}
                todayChangePct={data.today_change_pct}
                spyChangePct={spyChangePct}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BenchmarkRow({ label, changePct }: { label: string; changePct: number }) {
  return (
    <div className={`flex items-center justify-between py-2 px-3 rounded-xl ${changeBg(changePct)}`}>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-pink-400 flex-shrink-0" />
        <span className="text-sm font-semibold text-surface-100">{label}</span>
        <span className="text-xs text-surface-500">Benchmark</span>
      </div>
      <span className={`text-sm font-semibold font-mono tabular-nums ${changeColor(changePct)}`}>
        {formatPercent(changePct)}
      </span>
    </div>
  );
}

function AccountRow({ account, spyChangePct }: { account: AccountSummary; spyChangePct: number }) {
  const changePct = parseFloat(account.today_change_pct) || 0;
  const changeAmt = parseFloat(account.today_change) || 0;
  const value = parseFloat(account.portfolio_value) || 0;
  const alpha = changePct - spyChangePct;

  return (
    <div className={`flex items-center justify-between py-2 px-3 rounded-xl transition-colors ${changeBg(changePct)} hover:bg-surface-800/60`}>
      <div className="flex flex-col min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-surface-100 truncate">
            {account.account_name}
          </span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
            account.account_type === "live"
              ? "bg-accent-500/20 text-accent-300"
              : "bg-yellow-500/20 text-yellow-300"
          }`}>
            {account.account_type}
          </span>
        </div>
        <span className="text-xs text-surface-500">
          ${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </span>
      </div>
      <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
        {/* Daily P/L */}
        <div className="flex flex-col items-end">
          <span className={`text-sm font-semibold font-mono tabular-nums ${changeColor(changePct)}`}>
            {formatPercent(changePct)}
          </span>
          <span className={`text-xs font-mono tabular-nums ${changeColor(changeAmt)}`}>
            {changeAmt >= 0 ? "+" : ""}${Math.abs(changeAmt).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        {/* Alpha vs SPY */}
        <div className="flex flex-col items-end min-w-[52px]">
          <span className={`text-xs font-semibold font-mono tabular-nums ${changeColor(alpha)}`}>
            {alpha >= 0 ? "+" : ""}{alpha.toFixed(2)}%
          </span>
          <span className="text-[10px] text-surface-500">α</span>
        </div>
      </div>
    </div>
  );
}

function TotalRow({
  totalEquity,
  todayChange,
  todayChangePct,
  spyChangePct,
}: {
  totalEquity: string;
  todayChange: string;
  todayChangePct: string;
  spyChangePct: number;
}) {
  const changePct = parseFloat(todayChangePct) || 0;
  const changeAmt = parseFloat(todayChange) || 0;
  const equity = parseFloat(totalEquity) || 0;
  const alpha = changePct - spyChangePct;

  return (
    <div className={`flex items-center justify-between py-2 px-3 rounded-xl ${changeBg(changePct)}`}>
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-semibold text-surface-100">All Accounts</span>
        <span className="text-xs text-surface-500">
          ${equity.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </span>
      </div>
      <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
        <div className="flex flex-col items-end">
          <span className={`text-sm font-semibold font-mono tabular-nums ${changeColor(changePct)}`}>
            {formatPercent(changePct)}
          </span>
          <span className={`text-xs font-mono tabular-nums ${changeColor(changeAmt)}`}>
            {changeAmt >= 0 ? "+" : ""}${Math.abs(changeAmt).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="flex flex-col items-end min-w-[52px]">
          <span className={`text-xs font-semibold font-mono tabular-nums ${changeColor(alpha)}`}>
            {alpha >= 0 ? "+" : ""}{alpha.toFixed(2)}%
          </span>
          <span className="text-[10px] text-surface-500">α</span>
        </div>
      </div>
    </div>
  );
}
